import type { LayoutKind } from './types'

/** The editor's zoom: fitted to the stage, or a scale of the design's own pixels. */
export type Zoom = 'fit' | number

export const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const
export const MIN_ZOOM = ZOOM_STEPS[0]
export const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1]!

const within = (scale: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale))

/** The next step above (dir 1) or below (dir -1) a scale, which need not be a step itself. */
export function stepZoom(scale: number, dir: 1 | -1): number {
  const eps = 0.001
  if (dir > 0) return ZOOM_STEPS.find((s) => s > scale + eps) ?? MAX_ZOOM
  return [...ZOOM_STEPS].reverse().find((s) => s < scale - eps) ?? MIN_ZOOM
}

/** A scale after a wheel turn (deltaY in pixels, negative away from the reader). */
export function wheelZoom(scale: number, deltaY: number): number {
  return within(scale * Math.exp(-deltaY * 0.002))
}

/**
 * How far to scroll so the canvas point under the cursor stays there: the frame's
 * left (or top) edge on screen after the zoom, the point in canvas units, the new
 * scale and the cursor's screen position.
 */
export function anchorShift(frameEdge: number, point: number, scale: number, cursor: number): number {
  return frameEdge + point * scale - cursor
}

export const zoomKey = (kind: LayoutKind) => `qms.designs.zoom.${kind}`

export function parseZoom(raw: string | null): Zoom {
  const n = Number(raw)
  return raw && raw !== 'fit' && Number.isFinite(n) && n > 0 ? within(n) : 'fit'
}

/** The zoom last used on this kind of design in this browser. */
export function loadZoom(kind: LayoutKind): Zoom {
  try {
    return parseZoom(window.localStorage.getItem(zoomKey(kind)))
  } catch {
    return 'fit'
  }
}

export function saveZoom(kind: LayoutKind, zoom: Zoom) {
  try {
    window.localStorage.setItem(zoomKey(kind), String(zoom))
  } catch {
    // A browser that keeps nothing starts at Fit next time.
  }
}

/** Room around a zoomed design on each side, in stage widths/heights (studio.css). */
export const ROOM = 1

/**
 * The scroll that puts design point `point` (screen px from the design's edge) under
 * a cursor `cursor` px into a stage `view` px wide, with ROOM around the design, and
 * the most that can be scrolled. With ROOM 1 the first is always within the second.
 */
export function anchoredScroll(view: number, frame: number, point: number, cursor: number) {
  return { scroll: view * ROOM + point - cursor, max: frame + 2 * view * ROOM - view }
}
